// The Subscriptions dialog's saves (Seán's spec § 6, 2026-10-23): what's saved (confirmed) and
// what the switches show (desired). One save at a time, of the latest choice, so quick toggles
// can't overwrite each other; a switch whose change isn't saved yet is pending. A failed save
// puts the switches back as saved and can be retried.
import {computed, ref} from "vue";
import {LIST_KEYS, getOrdered} from "@/composables/useMailingListLink";

const getIsSame = (one, other) => one.join() === other.join();

// persist(lists): the lists now saved, or null when the save failed
export function useSubscriptionSaves(persist) {
  const confirmed = ref(null); // null until loaded
  const desired = ref([]);
  const [isSaving, isFailed] = [ref(false), ref(false)];
  let failedLists = null;
  const pendingKeys = computed(() => {
    const saved = confirmed.value || [];
    return LIST_KEYS.filter((key) => desired.value.includes(key) !== saved.includes(key));
  });
  const isPending = computed(() => isSaving.value || pendingKeys.value.length > 0);
  // the lists as saved elsewhere (loaded, refreshed): not while a change is on its way
  function load(lists) {
    if (isPending.value) return;
    confirmed.value = getOrdered(lists);
    desired.value = confirmed.value;
  }
  function fail(lists) {
    [failedLists, isFailed.value] = [lists, true];
    desired.value = confirmed.value;
  }
  async function flush() {
    if (isSaving.value) return;
    isSaving.value = true;
    while (!getIsSame(desired.value, confirmed.value)) {
      const lists = desired.value;
      const saved = await persist(lists);
      if (!saved || !getIsSame(getOrdered(saved), lists)) {
        fail(lists);
        break;
      }
      confirmed.value = lists;
    }
    isSaving.value = false;
  }
  function request(lists) {
    isFailed.value = false;
    desired.value = getOrdered(lists);
    return flush();
  }
  const retry = () => failedLists && request(failedLists);
  const reset = () => ([confirmed.value, desired.value, isFailed.value] = [null, [], false]);
  return {confirmed, desired, isSaving, isFailed, isPending, pendingKeys, load, request, retry,
    reset};
}
